// Lightweight API client wrapping fetch for consistent headers, errors, and parsing

const defaultJsonHeaders = { "Content-Type": "application/json" };

async function handleResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const payload = isJson
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    const message =
      (payload && (payload.message || payload.error || payload.detail)) ||
      `${response.status} ${response.statusText}`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

export async function getJson(url, options = {}) {
  const response = await fetch(url, {
    method: "GET",
    ...options,
    headers: { ...(options.headers || {}) },
  });
  return handleResponse(response);
}

export async function postJson(url, body, options = {}) {
  const response = await fetch(url, {
    method: "POST",
    headers: { ...defaultJsonHeaders, ...(options.headers || {}) },
    body: JSON.stringify(body ?? {}),
    ...options,
  });
  return handleResponse(response);
}

export async function postForm(url, formDataOrObject, options = {}) {
  let body;
  let headers = { ...(options.headers || {}) };
  if (
    formDataOrObject instanceof URLSearchParams ||
    formDataOrObject instanceof FormData
  ) {
    body = formDataOrObject;
  } else {
    body = new URLSearchParams(formDataOrObject || {});
    headers["Content-Type"] = "application/x-www-form-urlencoded";
  }

  const response = await fetch(url, {
    method: "POST",
    headers,
    body,
    ...options,
  });
  return handleResponse(response);
}

// Convenience helper for this app's middleware calls
export function getMiddlewareEndpoint(operation) {
  const baseEndpoint = process.env.REACT_APP_MIDDLEWARE_ENDPOINT;
  if (!baseEndpoint) {
    console.error("Middleware endpoint is not defined.");
    return "";
  }
  return `${baseEndpoint}&operation=${operation}`;
}

export async function callMiddleware(operation, payload, options = {}) {
  const endpoint = getMiddlewareEndpoint(operation);
  return postJson(endpoint, payload, options);
}

// Utility function to update work note
export const updateWorkNote = async (sysId, workNote) => {
  try {
    const endpoint = process.env.REACT_APP_UPDATE_WORKNOTE_ENDPOINT;
    const payload = {
      sys_id: sysId,
      work_notes: workNote,
    };
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      console.error("Failed to update worknote:", response.statusText);
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error updating worknote:", error);
    return false;
  }
};

//Utility function to update description
export const updateDescription = async (sysId, description) => {
  try {
    const endpoint = process.env.REACT_APP_UPDATE_DESCRIPTION_ENDPOINT;
    if (!endpoint) {
      throw new Error("Update description endpoint is not defined.");
    }
    const payload = {
      sys_id: sysId,
      description: description,
    };
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      console.error("Failed to update description:", response.statusText);
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`
      );
    }
  } catch (error) {
    console.error("Error updating description:", error);
    throw error;
  }
};
